import React from 'react';

import { PopoverProps } from '@mui/material/Popover';
import { TooltipProps } from '@mui/material/Tooltip';

import { Props as BoxProps } from '../Box/Box.jsx';
import Tooltip from '../Tooltip/Tooltip.jsx';
import {
  StyledFilterButtonContainer,
  StyledIconButton,
} from './FilterButton.styles.jsx';
import FilterButtonPopover from './FilterButtonPopover.jsx';
import { useControllableState } from './useControllableState.js';

export interface FilterButtonProps extends BoxProps {
  popoverContent?: React.ReactNode | null;
  popoverProps?: Partial<PopoverProps>;
  tooltipProps?: Partial<TooltipProps>;
  onClose?: () => void;
  onClick?: (...args: any) => void;
  popoverTitle?: string;
  anchorEl?: Element | null;
  onAnchorElChange?: (newAnchorEl: Element | null) => void;
  disabled?: boolean;
}

const FilterButton = ({
  children,
  onClick,
  popoverContent,
  popoverProps,
  tooltipProps,
  popoverTitle,
  onClose,
  anchorEl: controlledAnchorEl,
  onAnchorElChange,
  disabled,
  ...props
}: FilterButtonProps) => {
  const [anchorEl, setAnchorEl] = useControllableState(
    controlledAnchorEl,
    null,
  );

  const handleClick = (event: React.MouseEvent<Element>) => {
    if (onClick) {
      onClick(event);
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (onAnchorElChange) {
      onAnchorElChange(anchorEl);
    }
  }, [anchorEl, onAnchorElChange]);

  const IconButtonComponent = (
    <StyledIconButton
      icon="Filter"
      variant="icon"
      color="primary"
      size="medium"
      disabled={disabled}
      onClick={handleClick}
      sx={{
        '&.MuiIconButton-root.icon': {
          backgroundColor: (theme) =>
            anchorEl ? theme.palette.ui.active : theme.palette.transparent.main,
        },
      }}
    />
  );

  return (
    <>
      <StyledFilterButtonContainer {...props}>
        {disabled ? (
          <Tooltip title="disabled" placement="top-start" {...tooltipProps}>
            <span style={{ height: '100%' }}>{IconButtonComponent}</span>
          </Tooltip>
        ) : (
          IconButtonComponent
        )}
      </StyledFilterButtonContainer>
      <FilterButtonPopover
        open={Boolean(anchorEl)}
        onClose={handleClose}
        popoverProps={{ anchorEl: anchorEl, ...popoverProps }}
        popoverContent={popoverContent}
        popoverTitle={popoverTitle}
      />
    </>
  );
};

export default FilterButton;
